import React from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import LabelValueRow from "../common/LabelValueRow";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  vendor: {
    systemId: string;
    name: string;
    vatRegistrationNo: string;
    countryRegionCode?: string;
    email?: string;
    address?: string;
    city?: string;
    postCode?: string;
    autoAcceptLimit: number | null;
  };
  onSaveLimit: (value: number) => Promise<void>;
  onOpenBankAccounts: () => void;
};

export default function VendorHeader({ vendor, onSaveLimit, onOpenBankAccounts }: Props) {
  const { t } = useAppTranslation("common");
  const [limit, setLimit] = React.useState<number | "">(
    vendor.autoAcceptLimit ?? ""
  );
  const [saving, setSaving] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSave = async () => {
    if (limit === "" || Number.isNaN(Number(limit))) return;

    setSaving(true);
    setSuccess(false);
    setError(null);

    try {
      await onSaveLimit(Number(limit));
      setSuccess(true);
    } catch {
      setError(t("Nie udało się zapisać limitu automatycznej akceptacji"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          {/* NAZWA */}
          <Typography variant="h5">{vendor.name}</Typography>

          {/* DANE PODSTAWOWE */}
          <Stack spacing={1}>
            <LabelValueRow
                label={t("Nazwa")}
                value={vendor.name}
            />

            <LabelValueRow
                label={t("NIP")}
                value={vendor.vatRegistrationNo}
            />

            <LabelValueRow
                label={t("Kraj")}
                value={vendor.countryRegionCode}
            />

            <LabelValueRow
                label={t("Miasto")}
                value={vendor.city}
            />

            <LabelValueRow
                label={t("Kod pocztowy")}
                value={vendor.postCode}
            />

            <LabelValueRow
                label={t("Adres")}
                value={vendor.address}
            />
          </Stack>

          <Divider />

          {/* LIMIT */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label={t("Limit automatycznej akceptacji")}
              type="number"
              onFocus={(e) => {
                e.target.select();
              }}
              value={limit}
              onChange={(e) =>
                setLimit(e.target.value === "" ? "" : Number(e.target.value))
              }
              size="small"
            />

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || limit === vendor.autoAcceptLimit}
            >
              {saving ? <CircularProgress size={20} /> : t("Zapisz")}
            </Button>
          </Stack>

          <Divider />

          <Button
            variant="outlined"
            onClick={onOpenBankAccounts}
          >
            {t("Konta bankowe")}
          </Button>

          {/* FEEDBACK */}
          {success && (
            <Alert severity="success">
              {t("Limit automatycznej akceptacji został zapisany.")}
            </Alert>
          )}

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
